import type { JSX } from 'react';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import PhotoSize from '@alltrails/types/PhotoSize';
import StaticMapSize from '@alltrails/types/StaticMapSize';
import type Trail from '@alltrails/types/Trail';
import type TrailDetails from '@alltrails/types/TrailDetails';
import useTrailPhotos from '../../hooks/useTrailPhotos';
import type { CardProps, InternalCardProps } from '../../types';
import { getTrailUrl } from '../../utils/cardLinks';
import SmallCard from '../SmallCard';
import StandardCard from '../StandardCard';
import TrailMetadata from '../TrailMetadata';

type TrailCardVariant = 'small' | 'standard' | 'feed';

const getPhotoSize = (variant: TrailCardVariant, isMobile: boolean) => {
  switch (variant) {
    case 'small':
      return PhotoSize.LargeSquare;
    case 'feed':
      return isMobile ? PhotoSize.LargerWide : PhotoSize.ExtraLarge;
    case 'standard':
    default:
      return PhotoSize.LargerWide;
  }
};

const getMapSize = (variant: TrailCardVariant): StaticMapSize => {
  switch (variant) {
    case 'small':
      return '160x160';
    case 'standard':
    case 'feed':
    default:
      return '320x320';
  }
};

const getMaxPhotos = (variant: TrailCardVariant, maxPhotos: number) => {
  switch (variant) {
    case 'small':
      return 1;
    case 'standard':
    default:
      return Math.min(maxPhotos, 6);
  }
};

export type TrailCardProps = Omit<CardProps, 'useMetric' | 'decimalPlaces'> & {
  variant: TrailCardVariant;
  trail: Trail | TrailDetails;
  url?: string; // Allow overriding of the default link to the trail page
  isCompleted?: boolean;
  isVerified?: boolean;
  onCompletionBadgeClick?: () => void;
  metadata?: JSX.Element; // custom metadata element for trail cards in feed
  displayRank?: number; // 1-based index to display before the trail name
  blockNavigation?: boolean; // Prevent the trail links from actually navigating - the monolith handles all navigation from the onClick handlers
};

const TrailCard = ({
  className,
  variant,
  trail,
  url,
  languageRegionCode,
  onCardClick,
  onCardMouseEnter,
  onCardMouseLeave,
  onFavoriteClick,
  isFavorite,
  isCompleted,
  isVerified,
  openInNewTab,
  maxPhotos = 6,
  baseUrl = '',
  dragHandleProps,
  deleteButton,
  adminCTAs,
  onCompletionBadgeClick,
  metadata,
  displayRank,
  blockNavigation,
  lazyLoadImages
}: TrailCardProps) => {
  const { name, slug, id } = trail;
  const href = url || getTrailUrl(slug, baseUrl, languageRegionCode);
  const isMobile = useIsMobileSizedScreen();
  const { photos, loadPhotos } = useTrailPhotos(
    id,
    !!trail.defaultPhoto,
    getPhotoSize(variant, isMobile),
    getMapSize(variant),
    getMaxPhotos(variant, maxPhotos),
    baseUrl
  );

  const onMouseEnter = () => {
    loadPhotos();
    if (onCardMouseEnter) {
      onCardMouseEnter();
    }
  };

  const metadataElement = metadata || (
    <TrailMetadata variant={variant} trail={trail} baseUrl={baseUrl} openInNewTab={openInNewTab} displayRank={displayRank} />
  );

  const cardProps: InternalCardProps = {
    className,
    name,
    href,
    openInNewTab,
    metadata: metadataElement,
    onCardClick,
    onCardMouseEnter: onMouseEnter,
    onCardMouseLeave,
    onFavoriteClick,
    isFavorite,
    isCompleted,
    isVerified,
    onCompletionBadgeClick,
    blockNavigation,
    lazyLoadImages
  };
  switch (variant) {
    case 'small':
      return <SmallCard {...cardProps} photo={photos[0]} adminCTAs={adminCTAs} />;
    case 'standard':
    case 'feed':
    default:
      return <StandardCard {...cardProps} photos={photos} loadPhotos={loadPhotos} dragHandleProps={dragHandleProps} deleteButton={deleteButton} />;
  }
};

export default TrailCard;
